import { Button } from '@creditornot/cb-button';
import { FormHeader, FormRow } from '@creditornot/cb-form';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import React, { useContext } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled, { useTheme } from 'styled-components';

import { Option, OptionBinding } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { Typography as T } from 'common/typography';
import { localize } from 'common/utils';

import { findOption } from './utils';
import { MenuLocaleContext } from '../../../MenuLocaleContext';
import { MenuPermissionsContext } from '../../../components/MenuPermissionsContext';

type Props = {
  onStartEdit: () => void;
  option: Option;
  optionBinding: OptionBinding;
  otherItemOptions: Option[];
};

const Root = styled.div`
  user-select: text;
`;

const Header = styled.span`
  color: ${cbSemanticCssVars.textPrimary};
`;

const EditButton = styled(Button).attrs({
  size: 'small',
  variant: 'lightBlue',
})``;

const PaddedFormRow = styled(FormRow).attrs({ horizontalPadding: true })``;

const PrerequisitesWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const PrerequisiteOptions = ({
  prerequisiteChoiceIds,
  primaryLanguage,
  otherItemOptions,
}: {
  prerequisiteChoiceIds: string[];
  primaryLanguage: Language;
  otherItemOptions: Option[];
}) => {
  return (
    <PrerequisitesWrapper>
      {prerequisiteChoiceIds.map(choiceId => {
        const prerequisiteOption = findOption(otherItemOptions, choiceId);

        if (!prerequisiteOption) {
          return null;
        }

        const prerequisiteOptionChoice = prerequisiteOption.values.find(choice => choice.id === choiceId);

        return (
          <T.Body3 key={choiceId} dir="auto" lang={primaryLanguage}>
            {localize(prerequisiteOption.name, primaryLanguage)}
            {prerequisiteOptionChoice ? `: ${localize(prerequisiteOptionChoice.name, primaryLanguage)}` : ''}
          </T.Body3>
        );
      })}
    </PrerequisitesWrapper>
  );
};

const OptionBindingView = ({ onStartEdit, option, optionBinding, otherItemOptions }: Props) => {
  const intl = useIntl();
  const theme = useTheme();

  const menuPermissions = useContext(MenuPermissionsContext);
  const { primaryLanguage } = useContext(MenuLocaleContext);

  const optionBindingName = localize(optionBinding.name, primaryLanguage);

  return (
    <Root dir={theme.cbDirection}>
      <FormHeader
        horizontalPadding
        rightContent={
          <EditButton onClick={onStartEdit} disabled={!menuPermissions.contentEdit}>
            <FormattedMessage id="state.edit" />
          </EditButton>
        }
      >
        <Header>
          <FormattedMessage id="menu-option.details-header" />
        </Header>
      </FormHeader>

      <PaddedFormRow label={intl.formatMessage({ id: 'menu-option-instance.name' })}>
        <T.Body3 dir="auto" lang={primaryLanguage}>
          {optionBindingName.length > 0 ? optionBindingName : localize(option.name, primaryLanguage)}
        </T.Body3>
      </PaddedFormRow>

      {menuPermissions.posIdField && (
        <PaddedFormRow label={intl.formatMessage({ id: 'menu.pos-id' })}>
          {optionBinding.external_id || option.external_id || '-'}
        </PaddedFormRow>
      )}

      {option.type === 'multi_choice' && optionBinding.multi_choice_config && (
        <>
          <PaddedFormRow label={intl.formatMessage({ id: 'menu-option-instance.max-selections' })}>
            {optionBinding.multi_choice_config.total_selections.max}
          </PaddedFormRow>

          <PaddedFormRow label={intl.formatMessage({ id: 'menu-option-instance.max-single-selections' })}>
            {optionBinding.multi_choice_config.max_single_selections}
          </PaddedFormRow>

          <PaddedFormRow label={intl.formatMessage({ id: 'menu-option-instance.min-selections' })}>
            {optionBinding.multi_choice_config.total_selections.min}
          </PaddedFormRow>

          <PaddedFormRow label={intl.formatMessage({ id: 'menu-option-instance.free-selections' })}>
            {optionBinding.multi_choice_config.free_selections}
          </PaddedFormRow>
        </>
      )}

      <PaddedFormRow label={intl.formatMessage({ id: 'menu-option-instance.requiresOptionValue' })}>
        {optionBinding.prerequisite_values.length > 0 ? (
          <PrerequisiteOptions
            prerequisiteChoiceIds={optionBinding.prerequisite_values}
            primaryLanguage={primaryLanguage}
            otherItemOptions={otherItemOptions}
          />
        ) : (
          <FormattedMessage id="menu-option-instance.noRequiredOptions" />
        )}
      </PaddedFormRow>
    </Root>
  );
};

export default OptionBindingView;
